import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  getHostCallhistory,
  getHostGifthistory,
  getHostLivehistory,
} from "../store/history/history.action";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import dayjs from "dayjs";
import DateRangePicker from "react-bootstrap-daterangepicker";
import useTranslation from "../hooks/useTranslation";
import { pickerLocale, pickerDate } from "../util/pickerLocale";
import { groupNumber } from "../util/groupNumber";

const T = {
  pageBg: "var(--mx-bg)",
  text: "var(--mx-text)",
  sub: "var(--mx-text-2)",
  border: "var(--mx-border)",
  card: "var(--mx-card)",
  cardSolid: "var(--mx-veil)",
  radius: 16,
  shadow: "var(--mx-shadow-float)",
  // accents
  indigo: "var(--mx-accent)",
  purple: "var(--mx-accent)",
  green: "var(--mx-ok)",
  rose: "var(--mx-pink)",
  // grad* entries render flat (a same-color two-stop gradient); names kept for callers
  gradIndigo: "var(--mx-accent)",
  gradPink: "var(--mx-pink)",
  gradBlue: "var(--mx-info)",
  gradPurple: "var(--mx-accent)",
  gradPink2: "var(--mx-pink)",
  gradepurple2: "var(--mx-card)",
};

const HostHistory = () => {
  const { t, languageCode } = useTranslation();
  const { hostHitory, total } = useSelector((state) => state?.history);
  const dispatch = useDispatch();
  const location = useLocation();
  const [activePage, setActivePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loadingMore, setLoadingMore] = useState(false);
  const [type, setType] = useState("gift");
  const [startDate, setStartDate] = useState(dayjs().startOf("month").toDate());
  const [endDate, setEndDate] = useState(dayjs().endOf("month").toDate());

  const startAllDate = "1970-01-01";
  const endAllDate = dayjs().format("YYYY-MM-DD");
  // Reached from the host list via router state, but also directly from a saved or
  // shared link — without state the id was undefined and every request 500'd
  // ("input must be a 24 character hex string"). Accept ?hostId= as the fallback.
  const hostId =
    location?.state?.id ||
    location?.state ||
    new URLSearchParams(location?.search).get("hostId") ||
    null;
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    // Nothing selected yet: leave the lists empty instead of firing an
    // invalid-id request at the API.
    if (!hostId) return;
    const startDate = dayjs().startOf("month").format("YYYY-MM-DD");
    const endDate = dayjs().endOf("month").format("YYYY-MM-DD");
    if (type === "live") {
      dispatch(getHostLivehistory(hostId, startDate, endDate));
    } else if (type === "call") {
      dispatch(getHostCallhistory(hostId, startDate, endDate));
    } else {
      dispatch(
        getHostGifthistory(
          hostId,
          type,
          activePage,
          rowsPerPage,
          startDate,
          endDate,
        ),
      );
    }
  }, [hostId, type, activePage, rowsPerPage]);

  const handleApply = (event, picker) => {
    const start = dayjs(picker.startDate).format("YYYY-MM-DD");
    const end = dayjs(picker.endDate).format("YYYY-MM-DD");

    setStartDate(start);
    setEndDate(end);

    if (!hostId) return;
    if (type === "live") {
      dispatch(getHostLivehistory(hostId, start, end));
    } else if (type === "call") {
      dispatch(getHostCallhistory(hostId, start, end));
    } else {
      dispatch(
        getHostGifthistory(hostId, type, activePage, rowsPerPage, start, end),
      );
    }
  };

  const tabs = [
    { id: "gift", label: t("gift") },
    { id: "call", label: t("call") },
    { id: "live", label: t("live") },
  ];

  return (
    <>
      <div
        class="page"
        style={{
          background: "var(--mx-bg)",
          minHeight: "100vh",
        }}
      >
        {/* Enhanced Header */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 9,
            background: T.pageBg,
            padding: "14px 12px",
            borderBottom: `1px solid ${T.border}`,
            backdropFilter: "blur(10px)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: 18, color: T.text }}>
                {t("host_history")}
              </div>
              <div style={{ fontSize: 12, color: T.sub, marginTop: 2 }}>
                {t("host_earning")}
              </div>
            </div>
          </div>
        </div>

        <div class="main-wrapper">
          <div className="main-section" style={{ padding: "16px" }}>
            {/* Date Picker Section */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div style={{ minWidth: "215px", position: "relative" }}>
                <DateRangePicker
                  initialSettings={{
                    startDate: undefined,
                    endDate: undefined,
                    ranges: {
                      [t("all")]: [new Date("1970-01-01"), dayjs().toDate()],
                      [t("today")]: [dayjs().toDate(), dayjs().toDate()],
                      [t("yesterday")]: [
                        dayjs().subtract(1, "days").toDate(),
                        dayjs().subtract(1, "days").toDate(),
                      ],
                      [t("last_7_days")]: [
                        dayjs().subtract(6, "days").toDate(),
                        dayjs().toDate(),
                      ],
                      [t("last_30_days")]: [
                        dayjs().subtract(29, "days").toDate(),
                        dayjs().toDate(),
                      ],
                      [t("this_month")]: [
                        dayjs().startOf("month").toDate(),
                        dayjs().endOf("month").toDate(),
                      ],
                      [t("last_month")]: [
                        dayjs().subtract(1, "month").startOf("month").toDate(),
                        dayjs().subtract(1, "month").endOf("month").toDate(),
                      ],
                    },
                    maxDate: new Date(),
                    singleDatePicker: false,
                    linkedCalendars: false,
                    locale: pickerLocale(languageCode, t),
                  }}
                  onApply={handleApply}
                >
                  <input
                    type="text"
                    readOnly
                    placeholder={t("select_date_range")}
                    value={
                      (startDate === startAllDate && endDate === endAllDate) ||
                      (startDate === "All" && endDate === "All")
                        ? t("select_date_range")
                        : `${pickerDate(startDate, languageCode)} - ${pickerDate(
                            endDate,
                            languageCode,
                          )}`
                    }
                    style={{
                      width: "100%",
                      fontWeight: 500,
                      cursor: "pointer",
                      background: "var(--mx-border)",
                      color: T.text,
                      fontSize: "14px",
                      padding: "8px 12px",
                      borderRadius: "12px",
                      border: `1px solid ${T.border}`,
                      height: "48px",
                    }}
                  />
                </DateRangePicker>

                {/* Arrow Icon */}
                <span
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    fontSize: "14px",
                    color: T.text,
                  }}
                >
                  ▼
                </span>
              </div>
            </div>

            {/* Enhanced Tab Navigation */}
            <div
              style={{
                display: "flex",
                background: "rgba(55, 33, 67, 0.6)",
                padding: "6px",
                borderRadius: "12px",
                border: `1px solid ${T.border}`,
                marginBottom: "24px",
              }}
            >
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setType(tab.id)}
                  style={{
                    flex: 1,
                    background: type === tab.id ? T.gradPurple : "transparent",
                    border: "none",
                    padding: "12px 8px",
                    borderRadius: "8px",
                    color: type === tab.id ? T.text : T.sub,
                    fontWeight: "600",
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    minHeight: "44px",
                  }}
                >
                  <span className="tab-label">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Content Sections */}
            <div className="history-content">
              {!hostId && (
                // Opened stand-alone (no host picked, no ?hostId=): say so instead of
                // showing three empty tabs that look like a data outage.
                <div style={{ textAlign: "center", padding: "60px 20px", color: T.sub }}>
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      margin: "0 auto 16px",
                      borderRadius: "50%",
                      border: "1px solid var(--mx-border)",
                      background: "var(--mx-accent-soft)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="var(--mx-accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <line x1="20" y1="8" x2="20" y2="14" />
                      <line x1="23" y1="11" x2="17" y2="11" />
                    </svg>
                  </div>
                  <div style={{ color: T.text, fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                    {t("select_host", "Select a host")}
                  </div>
                  <div style={{ fontSize: 14, maxWidth: 300, margin: "0 auto", lineHeight: 1.5 }}>
                    {t(
                      "select_host_history",
                      "Open a host from the Hosts list to see their earnings history.",
                    )}
                  </div>
                </div>
              )}
              {hostId && type === "gift" && (
                <div>
                  {hostHitory?.length > 0 ? (
                    <div className="gift-list">
                      {hostHitory?.map((data, index) => (
                        <div
                          key={index}
                          className="history-card"
                          style={{
                            background: "var(--mx-card)",
                            borderRadius: "12px",
                            padding: "20px",
                            marginBottom: "12px",
                            border: `1px solid var(--mx-border)`,
                            transition: "all 0.3s ease",
                          }}
                          // onMouseEnter={(e) => {
                          //   e.currentTarget.style.transform = "translateY(-2px)";
                          //   e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
                          // }}
                          // onMouseLeave={(e) => {
                          //   e.currentTarget.style.transform = "translateY(0)";
                          //   e.currentTarget.style.boxShadow = "none";
                          // }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr auto auto",
                              gap: "16px",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  color: T.sub,
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  marginBottom: "4px",
                                }}
                              >
                                {t("username")}
                              </div>
                              <div
                                style={{
                                  color: T.text,
                                  fontWeight: 600,
                                  fontSize: "14px",
                                }}
                              >
                                {data?.name || "-"}
                              </div>
                            </div>

                            <div style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  color: T.sub,
                                  fontWeight: 600,
                                  fontSize: "14px",
                                }}
                              >
                                {t("coin_income")}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  src={require("../assets/images/rcoin.png")}
                                  style={{ height: "24px", width: "24px" }}
                                  alt="RCoin"
                                />
                                <span
                                  style={{
                                    color: T.text,
                                    fontWeight: 600,
                                    fontSize: "14px",
                                  }}
                                >
                                  {groupNumber(data?.totalRCoin, languageCode)}
                                </span>
                              </div>
                            </div>

                            <div style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  color: T.sub,
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  marginBottom: "4px",
                                }}
                              >
                                {t("date")}
                              </div>
                              <div
                                style={{
                                  color: T.text,
                                  fontWeight: 600,
                                  fontSize: "14px",
                                }}
                              >
                                {data?.date}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "60px 20px",
                        color: T.sub,
                      }}
                    >
                      <div style={{ marginBottom: 16, opacity: 0.6 }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="80"
                          height="80"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          viewBox="0 0 24 24"
                          style={{ color: T.sub }}
                        >
                          <path d="M6 2h9l5 5v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
                          <path d="M14 2v6h6" />
                          <line x1="8" y1="13" x2="16" y2="13" />
                          <line x1="8" y1="17" x2="16" y2="17" />
                        </svg>
                      </div>
                      <div
                        style={{
                          color: T.text,
                          fontSize: "18px",
                          fontWeight: "600",
                          marginBottom: "8px",
                        }}
                      >
                        {t("no_gift_history")}
                      </div>
                      <div style={{ fontSize: "14px" }}>
                        {t("gift_periods")}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {hostId && type === "call" && (
                <div>
                  {hostHitory?.length > 0 ? (
                    <div className="call-list">
                      {hostHitory?.map((data, index) => (
                        <div
                          key={index}
                          className="history-card"
                          style={{
                            background: "var(--mx-card)",
                            borderRadius: "12px",
                            padding: "20px",
                            marginBottom: "12px",
                            border: `1px solid var(--mx-border)`,
                            transition: "all 0.3s ease",
                          }}
                        >
                          <div style={{ marginBottom: "16px" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "8px",
                              }}
                            >
                              <div
                                style={{
                                  color: T.text,
                                  fontWeight: 600,
                                  fontSize: 14,
                                }}
                              >
                                {data?.userName || "-"}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                {data?.callType === "MissedCall" && (
                                  <span
                                    style={{
                                      background: "var(--mx-pink)",
                                      color: "white",
                                      padding: "4px 8px",
                                      borderRadius: "6px",
                                      fontWeight: 600,
                                      fontSize: 14,
                                    }}
                                  >
                                    {t("missed_call")}
                                  </span>
                                )}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <img
                                    src={require("../assets/images/rcoin.png")}
                                    style={{ height: "24px", width: "24px" }}
                                    alt="RCoin"
                                  />
                                  <span
                                    style={{
                                      color: T.text,
                                      fontWeight: 600,
                                      fontSize: 14,
                                    }}
                                  >
                                    {groupNumber(data?.coin, languageCode)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                color: T.sub,
                                fontWeight: 600,
                                fontSize: 14,
                              }}
                            >
                              <span>{data?.date?.split(",")[0]}</span>
                              <span>
                                {data?.callStartTime?.split(",")[1]} -{" "}
                                {data?.callEndTime?.split(",")[1] || "Ongoing"}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "60px 20px",
                        color: T.sub,
                      }}
                    >
                      <div style={{ marginBottom: 16, opacity: 0.6 }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="80"
                          height="80"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          viewBox="0 0 24 24"
                          style={{ color: T.sub }}
                        >
                          <path d="M6 2h9l5 5v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
                          <path d="M14 2v6h6" />
                          <line x1="8" y1="13" x2="16" y2="13" />
                          <line x1="8" y1="17" x2="16" y2="17" />
                        </svg>
                      </div>
                      <div
                        style={{
                          color: T.text,
                          fontSize: "18px",
                          fontWeight: "600",
                          marginBottom: "8px",
                        }}
                      >
                        {t("no_call_history")}
                      </div>
                      <div style={{ fontSize: "14px" }}>{t("call_record")}</div>
                    </div>
                  )}
                </div>
              )}

              {hostId && type === "live" && (
                <div>
                  {hostHitory?.length > 0 ? (
                    <div className="live-list">
                      {hostHitory?.map((data, index) => (
                        <div
                          key={index}
                          className="history-card"
                          style={{
                            background: "var(--mx-card)",
                            borderRadius: "12px",
                            padding: "20px",
                            marginBottom: "12px",
                            border: `1px solid var(--mx-border)`,
                            transition: "all 0.3s ease",
                          }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr 1fr 1fr",
                              gap: "12px",
                              alignItems: "center",
                              textAlign: "center",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  color: T.sub,
                                  fontWeight: 600,
                                  fontSize: 14,
                                  marginBottom: "4px",
                                }}
                              >
                                {t("duration")}
                              </div>
                              <div
                                style={{
                                  color: T.text,
                                  fontWeight: 600,
                                  fontSize: 14,
                                }}
                              >
                                {data?.duration}
                              </div>
                            </div>

                            <div>
                              <div
                                style={{
                                  color: T.sub,
                                  fontWeight: 600,
                                  fontSize: 14,
                                  marginBottom: "4px",
                                }}
                              >
                                {t("gift")}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  justifyContent: "center",
                                }}
                              >
                                <span
                                  style={{
                                    color: T.text,
                                    fontSize: "14px",
                                    fontWeight: "600",
                                  }}
                                >
                                  {data?.gifts || 0}
                                </span>
                              </div>
                            </div>

                            <div>
                              <div
                                style={{
                                  color: T.sub,
                                  fontWeight: 600,
                                  fontSize: 14,
                                  marginBottom: "4px",
                                }}
                              >
                                {t("rcoin")}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  src={require("../assets/images/rcoin.png")}
                                  style={{ height: "24px", width: "24px" }}
                                  alt="RCoin"
                                />
                                <span
                                  style={{
                                    color: T.text,
                                    fontSize: "14px",
                                    fontWeight: "600",
                                  }}
                                >
                                  {groupNumber(data?.rCoin, languageCode)}
                                </span>
                              </div>
                            </div>

                            <div>
                              <div
                                style={{
                                  color: T.sub,
                                  fontWeight: 600,
                                  fontSize: 14,
                                  marginBottom: "4px",
                                }}
                              >
                                {t("date")}
                              </div>
                              <div
                                style={{
                                  color: T.text,
                                  fontWeight: 600,
                                  fontSize: 14,
                                }}
                              >
                                {data?.startTime?.split(",")[0]}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "60px 20px",
                        color: T.sub,
                      }}
                    >
                      <div style={{ marginBottom: 16, opacity: 0.6 }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="80"
                          height="80"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          viewBox="0 0 24 24"
                          style={{ color: T.sub }}
                        >
                          <path d="M6 2h9l5 5v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
                          <path d="M14 2v6h6" />
                          <line x1="8" y1="13" x2="16" y2="13" />
                          <line x1="8" y1="17" x2="16" y2="17" />
                        </svg>
                      </div>
                      <div
                        style={{
                          color: T.text,
                          fontSize: "18px",
                          fontWeight: "600",
                          marginBottom: "8px",
                        }}
                      >
                        {t("no_live_history")}
                      </div>
                      <div style={{ fontSize: "14px" }}>
                        {t("no_live_stream")}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <style jsx>{`
          .history-card:hover {
            cursor: pointer;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          }

          /* Mobile Responsive Styles */
          @media (max-width: 768px) {
            .main-section {
              padding: 12px !important;
            }

            /* Show tab labels on mobile with smaller font */
            .tab-label {
              display: inline !important;
              font-size: 12px;
            }

            /* Adjust tab padding for mobile */
            .main-section > div > div:nth-child(2) button {
              padding: 10px 4px !important;
              gap: 4px !important;
              font-size: 12px !important;
            }

            .history-card {
              padding: 16px !important;
              margin-bottom: 10px !important;
            }

            /* Gift Section Mobile */
            .gift-list .history-card > div {
              grid-template-columns: 1fr !important;
              gap: 12px !important;
              text-align: left !important;
            }

            .gift-list .history-card > div > div {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }

            /* Call Section Mobile */
            .call-list .history-card > div > div:first-child {
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
            }

            .call-list .history-card > div > div:first-child > div:last-child {
              align-self: stretch;
              display: flex;
              justify-content: space-between;
            }

            /* Live Section Mobile */
            .live-list .history-card > div {
              grid-template-columns: 1fr 1fr !important;
              gap: 16px !important;
            }

            .live-list .history-card > div > div {
              text-align: center;
            }
          }

          @media (max-width: 480px) {
            .main-section {
              padding: 8px !important;
            }

            /* Further adjust tabs for very small screens */
            .main-section > div > div:nth-child(2) button {
              padding: 8px 2px !important;
              font-size: 11px !important;
            }

            .tab-label {
              font-size: 14px !important;
              font-weight: 600 !important;
            }

            .history-card {
              padding: 12px !important;
            }

            .live-list .history-card > div {
              grid-template-columns: 1fr !important;
              gap: 12px !important;
            }

            .live-list .history-card > div > div {
              display: flex;
              justify-content: space-between;
              align-items: center;
              text-align: left !important;
            }
          }

          /* Tablet Styles */
          @media (max-width: 1024px) and (min-width: 769px) {
            .gift-list .history-card > div {
              grid-template-columns: 1fr 1fr !important;
              gap: 20px !important;
            }

            .gift-list .history-card > div > div:last-child {
              grid-column: 1 / -1;
              justify-self: center;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default HostHistory;
